/* eslint-disable react/prop-types */
/**
 * Settlement Desk — what lands in the bank account, and when.
 *
 * Three stages, three different owners: NMI authorizes, NMI closes the nightly
 * batch, then TSYS/Nexio wires the money. NMI can answer the first two and has
 * no visibility into the third, so every deposit figure here is modelled and
 * labelled as such. See server/settlement-desk.js for the arithmetic and the
 * 27 real deposits it is fitted to.
 *
 * Layout: single responsive tree, no duplicated content.
 *   - md+  : settled batches render as a table.
 *   - <md  : the same batches render as stacked cards with label/value pairs,
 *            because a 7-column table on a phone is either cut off or scrolled
 *            sideways, and both hide money.
 * Nothing is allowed to overflow horizontally at any width.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Landmark,
  RefreshCw,
  AlertTriangle,
  ArrowDownToLine,
  Clock,
  Layers,
  ChevronDown,
  Info,
  Lock,
  XCircle,
  CreditCard,
} from 'lucide-react';
import { User } from '@/api/entities';
import { getApiUrl } from '@/lib/apiUrls';
import { railSplitCents, railSplitTotals, isMixedRailDay } from '@/utils/settlementRails';

/** Where the admin's funding-model choice persists. 'calendar' = the weekday
 *  calendar fitted to real deposits; a number forces a flat T+N. */
const FUNDING_DELAY_KEY = 'defera.settlementDesk.fundingDelayDays';
const DEFAULT_FUNDING_DELAY = 'calendar';

/** Shared surfaces, matching RiskReview so the admin pages read as one app. */
const PANEL =
  'rounded-2xl border border-white/[0.08] bg-zinc-950/60 backdrop-blur-2xl ' +
  'shadow-[0_24px_80px_-40px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.055)]';
const INSET = 'rounded-xl border border-white/[0.06] bg-white/[0.025]';

const usd = (cents) => {
  const value = (cents || 0) / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Math.abs(value));
  return value < 0 ? `−${formatted}` : formatted;
};

/** Plain YYYY-MM-DD gateway days — parsed as local calendar dates so no
 *  timezone can shift a batch onto the wrong row. */
const parseDay = (day) => {
  if (!day) return null;
  const [y, m, d] = String(day).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDay = (day) => {
  const d = parseDay(day);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
};

/** Step a gateway day, staying on the local calendar so a month or DST
 *  boundary can't land the result on the wrong date. */
const addDays = (day, count) => {
  const d = parseDay(day);
  if (!d) return null;
  d.setDate(d.getDate() + count);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDayLong = (day) => {
  const d = parseDay(day);
  return d
    ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '—';
};

/** "Tomorrow", "Monday", or a date — whichever reads fastest at a glance. */
const relativeDayLabel = (day, todayStr) => {
  const target = parseDay(day);
  const today = parseDay(todayStr);
  if (!target || !today) return formatDay(day);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff > 1 && diff < 7) return target.toLocaleDateString('en-US', { weekday: 'long' });
  return formatDay(day);
};

/** Modelled amounts carry a "~". A debit already leads with "−", so the two
 *  glyphs collide ("~−$448.74") without a hair space between them. */
const modelledUsd = (cents, estimated) =>
  `${estimated ? `~${cents < 0 ? ' ' : ''}` : ''}${usd(cents)}`;

const sourceLabel = (s) => {
  if (s.kind === 'open_batch') return `tonight's batch · ${s.count} txn`;
  if (s.kind === 'reserve_release') return 'reserve released in full';
  // Stripe states what it is sending and when, so these rows name the payout
  // rather than a modelled batch — there is nothing to estimate on this rail.
  if (s.kind === 'stripe_payout') {
    return `Stripe payout${s.bankLast4 ? ` · •••${s.bankLast4}` : ''}${s.status === 'pending' ? ' · pending' : ''}`;
  }
  if (s.kind === 'stripe_balance') return 'Stripe balance · next sweep';
  return `batch ${s.processorBatchId} · closed ${formatDay(s.settledOn)}`;
};

/** Which processor a row's money comes through. Shown as a chip because the
 *  two rails behave differently — NMI deposits are modelled from a fitted
 *  calendar, Stripe payouts are facts — and a reader must know which they
 *  are looking at before trusting the date. */
const RAIL_CHIP = {
  nmi: 'text-brand-200/80 border-brand-300/20 bg-brand-400/[0.07]',
  stripe: 'text-violet-200/80 border-violet-300/20 bg-violet-400/[0.07]',
};

function RailChip({ rail }) {
  if (!rail) return null;
  return (
    <span
      className={`inline-flex items-center rounded-md border px-1.5 py-[1px] text-[10px] uppercase tracking-[0.1em] font-medium ${
        RAIL_CHIP[rail] || RAIL_CHIP.nmi
      }`}
    >
      {rail === 'stripe' ? 'Stripe' : 'NMI'}
    </span>
  );
}

/* ── Primitives ─────────────────────────────────────────────────────────── */

const TONE = {
  good: { grad: 'from-emerald-400/25 via-white/[0.06] to-transparent', label: 'text-emerald-300/75', value: 'text-emerald-200' },
  bad: { grad: 'from-rose-400/25 via-white/[0.06] to-transparent', label: 'text-rose-300/75', value: 'text-rose-200' },
  brand: { grad: 'from-brand-300/25 via-white/[0.06] to-transparent', label: 'text-brand-200/75', value: 'text-white/90' },
  neutral: { grad: 'from-white/[0.10] via-white/[0.04] to-transparent', label: 'text-stone-400', value: 'text-white/90' },
};

function StatTile({ icon: Icon, label, value, sub, tone = 'neutral' }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div className={`rounded-2xl p-px bg-gradient-to-br ${t.grad}`}>
      <div className="rounded-2xl bg-zinc-950/85 border border-white/[0.055] px-3.5 py-3 h-full flex flex-col gap-1.5">
        <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-medium ${t.label}`}>
          <Icon className="w-3 h-3 shrink-0" strokeWidth={1.5} />
          <span className="min-w-0 truncate">{label}</span>
        </div>
        <p className={`text-[26px] sm:text-3xl font-extralight leading-none tabular-nums ${t.value} break-words`}>
          {value}
        </p>
        {sub && <p className="text-white/40 text-xs font-normal leading-snug">{sub}</p>}
      </div>
    </div>
  );
}

function Segmented({ options, value, onChange, ariaLabel }) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center rounded-full bg-white/[0.04] border border-white/[0.08] p-0.5 max-w-full overflow-hidden"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 h-8 rounded-full text-[13px] font-medium transition whitespace-nowrap ${
            String(value) === String(o.value)
              ? 'bg-white/[0.10] text-white'
              : 'text-white/55 hover:text-white'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A label/value pair that stacks rather than truncates. */
function Figure({ label, value, tone = 'text-white/85', className = '' }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <p className="text-white/40 text-[11px] font-normal uppercase tracking-[0.1em]">{label}</p>
      <p className={`text-[15px] font-normal tabular-nums break-words ${tone}`}>{value}</p>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export default function SettlementDesk() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [desk, setDesk] = useState(null);
  const [days, setDays] = useState(30);
  const [fundingDelayDays, setFundingDelayDays] = useState(() => {
    // Guard the absent case explicitly: Number(null) is 0, which passes a bare
    // range check and would silently default the desk to same-day funding.
    const raw = localStorage.getItem(FUNDING_DELAY_KEY);
    if (raw == null || raw === '' || raw === 'calendar') return DEFAULT_FUNDING_DELAY;
    const stored = Number(raw);
    return Number.isInteger(stored) && stored >= 0 && stored <= 5
      ? String(stored)
      : DEFAULT_FUNDING_DELAY;
  });
  const [expanded, setExpanded] = useState(null);
  const [openBatchShown, setOpenBatchShown] = useState(false);

  const canAccess =
    !!currentUser &&
    (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  useEffect(() => {
    localStorage.setItem(FUNDING_DELAY_KEY, String(fundingDelayDays));
  }, [fundingDelayDays]);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (!canAccess) return;
      try {
        setIsRefreshing(true);
        setError(null);
        const { supabase } = await import('@/lib/supabase');
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');

        const params = new URLSearchParams({
          days: String(days),
          fundingDelayDays: String(fundingDelayDays), // 'calendar' or a flat T+N
        });
        if (force) params.set('refresh', '1');

        const resp = await fetch(getApiUrl(`admin/settlement-desk?${params.toString()}`), {
          headers: { Authorization: `Bearer ${session.access_token}` },
          credentials: 'omit',
        });
        const json = await resp.json();
        if (!resp.ok || !json.ok) throw new Error(json?.error || `Request failed (${resp.status})`);
        setDesk(json);
      } catch (err) {
        setError(err?.message || 'Could not load the settlement desk.');
      } finally {
        setIsRefreshing(false);
        setIsLoading(false);
      }
    },
    [canAccess, days, fundingDelayDays],
  );

  useEffect(() => {
    (async () => {
      try {
        setCurrentUser(await User.me());
      } catch {
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (canAccess) load();
  }, [canAccess, load]);

  /** Days carrying actual batch settlement, excluding a reserve-only day. */
  const settlementDepositCount = useMemo(
    () => (desk?.upcoming || []).filter((u) => u.sources.some((s) => s.kind !== 'reserve_release')).length,
    [desk],
  );

  /** "Landing soon" answers what reaches the bank, so it reads BOTH rails.
   *  Older cached responses (or an NMI-only desk) have no `allRails`, so fall
   *  back to the NMI list rather than rendering an empty panel. */
  const railUpcoming = useMemo(
    () => desk?.allRails?.upcoming || desk?.upcoming || [],
    [desk],
  );

  /** Tomorrow across both rails — a Stripe payout landing tomorrow is money
   *  landing tomorrow, and the tile would lie if it only counted NMI. */
  const landingTomorrow = useMemo(() => {
    if (!desk) return null;
    const tomorrow = addDays(desk.today, 1);
    return railUpcoming.find((u) => u.day === tomorrow) || null;
  }, [desk, railUpcoming]);

  /** What each rail contributes to "landing soon". Summed from the rows on
   *  screen so the header can never disagree with the list under it. */
  const railTotals = useMemo(() => railSplitTotals(railUpcoming), [railUpcoming]);
  const tomorrowSplit = useMemo(
    () => (landingTomorrow && isMixedRailDay(landingTomorrow) ? railSplitCents(landingTomorrow.sources) : null),
    [landingTomorrow],
  );

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <p className="text-white/60 font-normal">Loading…</p>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <p className="text-white/85 font-normal">Administrator access required.</p>
      </div>
    );
  }

  const a = desk?.assumptions || {};
  const tomorrowIsDebit = (landingTomorrow?.amountCents ?? 0) < 0;

  return (
    <>
      <Helmet>
        <title>Settlement Desk - Defera</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-5 sm:py-7 overflow-x-hidden">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-brand-500/[0.12] border border-brand-400/20 flex items-center justify-center shrink-0">
              <Landmark className="w-4 h-4 text-brand-200" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extralight text-white leading-tight">
                Settlement Desk
              </h1>
              <p className="text-white/45 text-[13px] font-normal leading-snug">
                Authorization → nightly batch → the wire that hits the bank.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Controls — wrap freely, never squeeze */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-5">
          <Segmented
            ariaLabel="Window"
            value={days}
            onChange={setDays}
            options={[
              { value: 14, label: '14d' },
              { value: 30, label: '30d' },
              { value: 90, label: '90d' },
            ]}
          />
          <div className="relative">
            <select
              value={fundingDelayDays}
              onChange={(e) => setFundingDelayDays(e.target.value)}
              aria-label="Funding model"
              className="appearance-none h-9 pl-3.5 pr-9 rounded-full bg-white/[0.04] border border-white/[0.08] text-[13px] font-medium text-white/80 focus:outline-none focus:border-brand-400/40"
            >
              <option value="calendar">Fitted calendar</option>
              <option value="1">Flat T+1</option>
              <option value="2">Flat T+2</option>
              <option value="3">Flat T+3</option>
              <option value="4">Flat T+4</option>
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-white/40 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <button
            type="button"
            onClick={() => load({ force: true })}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[13px] font-medium text-white/70 hover:text-white transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-rose-400/25 bg-rose-500/[0.06] px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
            <p className="text-rose-100/90 text-[13.5px] font-normal min-w-0 break-words">{error}</p>
          </div>
        )}

        {desk && (
          <>
            {/* Provenance — the page must never pass a model off as a bank fact */}
            <div className="mb-5 rounded-2xl border border-brand-400/20 bg-brand-500/[0.05] px-4 py-3.5 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-brand-200/80 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0 space-y-1.5">
                <p className="text-white/85 text-[13.5px] font-normal">
                  Deposits are modelled, not confirmed.
                </p>
                <p className="text-white/50 text-[13px] font-normal leading-relaxed">
                  NMI reports what settled; it never sees the wire. Sales less{' '}
                  {((a.processingRate || 0) * 100).toFixed(2)}% discount, then{' '}
                  {((a.reserveRate || 0) * 100).toFixed(0)}% reserve on the batch net — fitted to 27
                  real Nexio deposits and accurate to the cent.
                </p>
                {/* An empty failure list means nothing if no batch events are
                    arriving. Say which it is, rather than letting silence read
                    as an all-clear. */}
                {!desk.webhooks?.subscribed && (
                  <p className="text-amber-200/70 text-[13px] font-normal leading-relaxed">
                    No batch-level webhook events have arrived yet, so a failed batch would not be
                    detected here. Subscribe to{' '}
                    <span className="text-amber-100/80">settlement.batch.failure</span> in the
                    Vector Payments portal — see docs/nmi-settlement-webhooks.md.
                  </p>
                )}
                <p className="text-white/50 text-[13px] font-normal leading-relaxed">
                  {a.fundingModel === 'fitted_weekday_calendar' ? (
                    <>
                      Dates use the weekday funding calendar fitted since{' '}
                      {formatDay(a.fundingFittedFrom)} — 24 of 25 exact. A batch funds{' '}
                      {a.fundingLagRange?.minDays}–{a.fundingLagRange?.maxDays} days after it
                      closes, so read any date as ±1 day.
                    </>
                  ) : (
                    <>Dates use a flat T+{a.fundingDelayDays} business-day lag.</>
                  )}
                </p>
              </div>
            </div>

            {/* Failed batches lead the page. This is money the processor did
                not take, and it is invisible to query.php — a failed batch has
                no settle action, so it never appears in the table below. */}
            {desk.settlementFailures?.length > 0 && (
              <div className="mb-5 rounded-2xl border border-rose-400/35 bg-rose-500/[0.08] px-4 py-3.5">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
                  <div className="min-w-0 w-full">
                    <p className="text-rose-100 text-[14px] font-medium">
                      {desk.settlementFailures.length} batch
                      {desk.settlementFailures.length === 1 ? '' : 'es'} failed to settle
                    </p>
                    <p className="text-rose-100/60 text-[13px] font-normal mt-1 leading-relaxed">
                      The gateway reported these as not handed to the processor, so they will not
                      fund. They do not appear in the table below — a failed batch never receives a
                      settle action.
                    </p>
                    <div className="mt-2.5 space-y-1.5">
                      {desk.settlementFailures.map((f) => (
                        <div
                          key={`${f.kind}-${f.batchId}`}
                          className="rounded-xl bg-black/25 border border-rose-400/20 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3"
                        >
                          <div className="min-w-0">
                            <p className="text-rose-50/90 text-[13.5px] font-normal">
                              Batch {f.batchId}
                              {f.kind === 'chargeback' && (
                                <span className="text-rose-200/60 ml-2">chargeback</span>
                              )}
                            </p>
                            <p className="text-rose-100/45 text-[11.5px] font-normal break-words">
                              {f.processorName || 'unknown processor'}
                              {f.alertedAt ? ' · support@ alerted' : ' · alert not yet sent'}
                            </p>
                          </div>
                          <p className="text-rose-200 text-[14px] font-normal tabular-nums shrink-0">
                            {f.amountCents == null ? 'amount not reported' : usd(f.amountCents)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {desk.nexio?.error && (
              <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <p className="text-amber-100/90 text-[13.5px] font-normal min-w-0 break-words">
                  Nexio is configured but returned: {desk.nexio.error}
                </p>
              </div>
            )}

            {/* Headline */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mb-3">
              <StatTile
                icon={ArrowDownToLine}
                tone={landingTomorrow ? (tomorrowIsDebit ? 'bad' : 'good') : 'neutral'}
                label={tomorrowIsDebit ? 'Out tomorrow' : 'Landing tomorrow'}
                value={landingTomorrow ? usd(landingTomorrow.amountCents) : 'Nothing'}
                sub={
                  landingTomorrow
                    ? tomorrowSplit
                      ? `${formatDayLong(landingTomorrow.day)} · NMI ${usd(tomorrowSplit.nmiCents)} · Stripe ${usd(tomorrowSplit.stripeCents)}`
                      : formatDayLong(landingTomorrow.day)
                    : 'Nothing funds tomorrow, either rail'
                }
              />
              {/* Settlement only — the reserve release is a one-off and would
                  dominate this figure, then vanish the day it lands. */}
              <StatTile
                icon={Layers}
                label="Settlement in flight"
                value={usd(desk.totals.inFlightCents)}
                sub={`NMI rail · ${settlementDepositCount} batch deposit${settlementDepositCount === 1 ? '' : 's'} pending`}
              />
              <StatTile
                icon={Clock}
                label="Open batch"
                value={usd(desk.openBatch.netCents)}
                sub={`${desk.openBatch.count} txn · closes ${relativeDayLabel(desk.openBatch.closesOn, desk.today).toLowerCase()} 11PM`}
              />
            </div>

            {/* Reserve — invisible in NMI; only shows as the gap between what
                settled and what arrived. Stacks cleanly on a phone. */}
            {desk.reserve?.active && (
              <div className={`${PANEL} px-4 py-4 mb-5`}>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-medium text-brand-200/75">
                      <Lock className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                      Reserve held by the processor
                    </div>
                    <p className="text-3xl font-extralight text-white/90 mt-1.5 tabular-nums">
                      {usd(desk.reserve.heldCents)}
                    </p>
                    <p className="text-white/40 text-xs font-normal mt-1 leading-snug">
                      {(desk.reserve.rate * 100).toFixed(0)}% withheld on every batch since{' '}
                      {formatDay(desk.reserve.startDay)}
                      {!desk.reserve.complete && (
                        <span className="text-amber-300/80">
                          {' '}· partial — window misses the first batch
                        </span>
                      )}
                    </p>
                  </div>
                  <div className={`${INSET} px-3.5 py-3 sm:text-right shrink-0`}>
                    <p className="text-white/40 text-[11px] uppercase tracking-[0.1em] font-normal">
                      Releases in full
                    </p>
                    <p className="text-emerald-300 text-[17px] font-normal mt-0.5">
                      {formatDayLong(desk.reserve.releaseDay)}
                    </p>
                    <p className="text-white/35 text-[11px] font-normal mt-1">
                      one lump, then withholding stops
                    </p>
                    <p className="text-white/35 text-[11px] font-normal">
                      {usd(desk.totals.incomingCents)} NMI incoming all-in
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Landing soon — BOTH rails, because the question is what reaches
                the bank, not what one processor is doing. */}
            <div className={`${PANEL} px-4 py-4 mb-3`}>
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-3">
                <h2 className="text-white/85 text-[15px] font-medium">Landing soon</h2>
                <p className="text-white/40 text-[11.5px] font-normal">
                  {usd(desk.allRails?.totals?.incomingCents ?? desk.totals.incomingCents)} incoming
                  {desk.allRails?.rails
                    ? desk.allRails.rails.stripe
                      ? ` · NMI ${usd(railTotals.nmiCents)} · Stripe ${usd(railTotals.stripeCents)}`
                      : ' · NMI only (Stripe rail unavailable)'
                    : ''}
                </p>
              </div>
              {railUpcoming.length === 0 ? (
                <p className="text-white/45 text-[13.5px] font-normal">Nothing in flight right now.</p>
              ) : (
                <div className="space-y-2">
                  {railUpcoming.map((day) => {
                    const isDebit = day.amountCents < 0;
                    return (
                      <div
                        key={day.day}
                        className={`${INSET} px-3.5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3`}
                      >
                        <div className="min-w-0">
                          <p className="text-white/85 text-[14px] font-normal flex items-center gap-2 flex-wrap">
                            <span>
                              {relativeDayLabel(day.day, desk.today)}
                              {relativeDayLabel(day.day, desk.today) !== formatDay(day.day) && (
                                <span className="text-white/35 ml-2">{formatDay(day.day)}</span>
                              )}
                            </span>
                            {[...new Set((day.sources || []).map((src) => src.rail || 'nmi'))].map((rail) => (
                              <RailChip key={rail} rail={rail} />
                            ))}
                          </p>
                          <p className="text-white/40 text-xs font-normal leading-snug break-words">
                            {day.sources.map(sourceLabel).join(' · ')}
                          </p>
                        </div>
                        <div className="shrink-0 sm:text-right">
                          <p
                            className={`text-[19px] font-light tabular-nums ${
                              isDebit ? 'text-rose-300' : 'text-emerald-300'
                            }`}
                          >
                            {usd(day.amountCents)}
                          </p>
                          {isMixedRailDay(day) && (() => {
                            // Both rails land today: the chips say which two,
                            // this says how much of the figure is each.
                            const split = railSplitCents(day.sources);
                            return (
                              <p className="text-white/45 text-[11px] font-normal tabular-nums">
                                NMI {usd(split.nmiCents)} · Stripe {usd(split.stripeCents)}
                              </p>
                            );
                          })()}
                          <p className="text-white/35 text-[11px] font-normal">
                            {(day.sources || []).some((src) => src.estimated ?? day.estimated)
                              ? (day.sources || []).every((src) => src.estimated ?? day.estimated)
                                ? 'modelled'
                                : 'part modelled'
                              : 'confirmed'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Stripe rail. Deliberately its own panel rather than columns beside
                the NMI figures: the two rails answer the same question with
                different certainty, and putting a fitted deposit estimate next
                to a stated payout amount invites reading both as facts. */}
            {desk.stripe && (
              <div className={`${PANEL} px-4 py-4 mb-3`}>
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-3">
                  <h2 className="text-white/85 text-[15px] font-medium flex items-center gap-2">
                    <CreditCard className="w-3.5 h-3.5 text-violet-300/70 shrink-0" strokeWidth={1.5} />
                    Stripe rail
                  </h2>
                  <p className="text-white/40 text-[11.5px] font-normal">
                    {desk.stripe.schedule.manual
                      ? 'manual payouts — nothing arrives on its own'
                      : `payouts ${desk.stripe.schedule.interval}, T+${desk.stripe.schedule.delayDays}`}
                  </p>
                </div>

                {!desk.stripe.configured ? (
                  <p className="text-white/45 text-[13.5px] font-normal">
                    Stripe is not configured on this environment — the desk is showing the NMI rail only.
                  </p>
                ) : desk.stripe.error ? (
                  <div className="flex items-start gap-2.5 rounded-xl border border-amber-400/25 bg-amber-500/[0.06] px-3.5 py-3">
                    <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                    <p className="text-amber-100/90 text-[13.5px] font-normal min-w-0 break-words">
                      Stripe returned: {desk.stripe.error}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div className={`${INSET} px-3.5 py-3`}>
                        <Figure
                          label="Payouts in transit"
                          value={usd(desk.stripe.totals.inTransitCents)}
                          tone="text-emerald-300"
                        />
                        <p className="text-white/35 text-[11px] font-normal mt-1">
                          {desk.stripe.totals.payoutCount} payout
                          {desk.stripe.totals.payoutCount === 1 ? '' : 's'} Stripe has committed to
                        </p>
                      </div>
                      <div className={`${INSET} px-3.5 py-3`}>
                        <Figure
                          label="Held, not yet paid out"
                          value={usd(desk.stripe.balance.pendingCents)}
                        />
                        <p className="text-white/35 text-[11px] font-normal mt-1">
                          {desk.stripe.totals.projectedCents > 0
                            ? `sweeps as ~${usd(desk.stripe.totals.projectedCents)}`
                            : 'nothing to sweep yet'}
                        </p>
                      </div>
                      <div className={`${INSET} px-3.5 py-3`}>
                        <Figure
                          label="Available balance"
                          value={usd(desk.stripe.balance.availableCents)}
                          tone={desk.stripe.balance.availableIsNegative ? 'text-rose-300' : 'text-white/85'}
                        />
                        <p className="text-white/35 text-[11px] font-normal mt-1 leading-snug">
                          {desk.stripe.balance.availableIsNegative
                            ? 'owed back — netted out of the next payout'
                            : 'swept on the payout schedule'}
                        </p>
                      </div>
                    </div>

                    {desk.stripe.failures.length > 0 && (
                      <div className="mt-3 rounded-xl border border-rose-400/25 bg-rose-500/[0.06] px-3.5 py-3">
                        <p className="text-rose-100/90 text-[13px] font-medium mb-1.5">
                          {desk.stripe.failures.length} payout
                          {desk.stripe.failures.length === 1 ? '' : 's'} never arrived
                        </p>
                        <div className="space-y-1">
                          {desk.stripe.failures.map((f) => (
                            <p key={f.id} className="text-rose-100/60 text-[11.5px] font-normal break-words">
                              {formatDay(f.day)} · {usd(f.amountCents)} · {f.status}
                              {f.failureCode ? ` · ${f.failureCode}` : ''}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {desk.stripe.recent.length > 0 && (
                      <div className="mt-3">
                        <p className="text-white/40 text-[11px] uppercase tracking-[0.12em] font-medium mb-1.5">
                          Recently landed
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {desk.stripe.recent.map((r) => (
                            <span
                              key={r.id}
                              className="inline-flex items-baseline gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.025] px-2 py-1"
                            >
                              <span className="text-white/40 text-[11px] font-normal">{formatDay(r.day)}</span>
                              <span className="text-white/85 text-[12.5px] font-normal tabular-nums">
                                {usd(r.amountCents)}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Open batch */}
            <div className={`${PANEL} px-4 py-4 mb-3`}>
              <button
                type="button"
                onClick={() => setOpenBatchShown((v) => !v)}
                className="w-full flex items-start justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <h2 className="text-white/85 text-[15px] font-medium">
                    Open batch — authorized, not settled
                  </h2>
                  <p className="text-white/45 text-xs font-normal mt-1 leading-snug break-words">
                    {desk.openBatch.count} transaction{desk.openBatch.count === 1 ? '' : 's'} ·
                    closes {formatDayLong(desk.openBatch.closesOn)} · wires{' '}
                    {usd(desk.openBatch.estimatedDepositCents)} on{' '}
                    {formatDay(desk.openBatch.estimatedDepositDay)}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-white/40 shrink-0 mt-0.5 transition-transform ${
                    openBatchShown ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {openBatchShown && (
                <div className="mt-3.5 space-y-1.5">
                  {desk.openBatch.items.length === 0 && (
                    <p className="text-white/45 text-[13.5px] font-normal">The batch is empty.</p>
                  )}
                  {desk.openBatch.items.map((item) => (
                    <div
                      key={item.transactionId}
                      className={`${INSET} px-3 py-2.5 flex items-center justify-between gap-3`}
                    >
                      <div className="min-w-0">
                        <p className="text-white/85 text-[13.5px] font-normal truncate">
                          {item.customerName || 'Unknown customer'}
                          {item.bookingReference && (
                            <span className="text-brand-200 ml-2">{item.bookingReference}</span>
                          )}
                        </p>
                        <p className="text-white/35 text-[11.5px] font-normal truncate">
                          {item.description || item.transactionId}
                          {item.cardLast4 ? ` · ••${item.cardLast4}` : ''}
                        </p>
                      </div>
                      <p
                        className={`text-[13.5px] font-normal tabular-nums shrink-0 ${
                          item.isRefund ? 'text-rose-300' : 'text-white/85'
                        }`}
                      >
                        {usd(item.amountCents)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Settled batches */}
            <div className={`${PANEL} px-4 py-4`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
                <h2 className="text-white/85 text-[15px] font-medium">Settled batches</h2>
                <p className="text-white/35 text-[11.5px] font-normal">
                  {desk.totals.batchCount} batches · {desk.totals.settledCount} txns
                  {desk.totals.declinedCount > 0 && (
                    <span className="text-rose-300/60 ml-2 inline-flex items-center gap-1">
                      <XCircle className="w-3 h-3" />
                      {desk.totals.declinedCount} declined
                    </span>
                  )}
                </p>
              </div>

              {/* Mobile: stacked cards. A 7-column table on a phone either
                  clips or scrolls sideways, and both hide money. */}
              <div className="md:hidden space-y-2">
                {desk.batches.map((b) => {
                  const open = expanded === b.batchId;
                  return (
                    <div key={b.batchId} className={`${INSET} overflow-hidden`}>
                      <button
                        type="button"
                        onClick={() => setExpanded(open ? null : b.batchId)}
                        className="w-full px-3.5 py-3 text-left"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-white/85 text-[14px] font-normal">
                              {formatDay(b.day)}
                              <span className="text-white/35 ml-2">batch {b.processorBatchId}</span>
                            </p>
                            <p className="text-white/35 text-[11.5px] font-normal">
                              {b.count} txn{b.refundCount ? ` · ${b.refundCount} refund` : ''}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className={`text-[17px] font-light tabular-nums ${
                                b.deposit.amountCents < 0 ? 'text-rose-300' : 'text-emerald-300'
                              }`}
                            >
                              {modelledUsd(b.deposit.amountCents, b.deposit.estimated)}
                            </p>
                            <p className="text-white/35 text-[11px] font-normal">
                              {formatDay(b.deposit.day)}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mt-3 pt-3 border-t border-white/[0.06]">
                          <Figure label="Sales" value={usd(b.salesCents)} />
                          <Figure
                            label="Refunds"
                            value={b.refundsCents ? usd(b.refundsCents) : '—'}
                            tone={b.refundsCents ? 'text-rose-300/85' : 'text-white/35'}
                          />
                          <Figure
                            label="Net"
                            value={usd(b.netCents)}
                            tone={b.netCents < 0 ? 'text-rose-300' : 'text-white/85'}
                          />
                          <Figure
                            label="Reserve"
                            value={b.reserveHeldCents ? `−${usd(b.reserveHeldCents)}` : '—'}
                            tone={b.reserveHeldCents ? 'text-brand-200/85' : 'text-white/35'}
                          />
                        </div>
                      </button>

                      {open && (
                        <div className="px-3.5 pb-3.5 space-y-1.5">
                          {b.items.map((item) => (
                            <div
                              key={`${b.batchId}-${item.transactionId}`}
                              className="rounded-lg bg-white/[0.03] px-3 py-2 flex items-center justify-between gap-3"
                            >
                              <div className="min-w-0">
                                <p className="text-white/80 text-[13px] font-normal truncate">
                                  {item.customerName || 'Unknown'}
                                  {item.bookingReference && (
                                    <span className="text-brand-200 ml-1.5">
                                      {item.bookingReference}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <p
                                className={`text-[13px] font-normal tabular-nums shrink-0 ${
                                  item.isRefund ? 'text-rose-300' : 'text-white/80'
                                }`}
                              >
                                {usd(item.amountCents)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* md+: the table */}
              <div className="hidden md:block">
                <table className="w-full text-[13.5px]">
                  <thead>
                    <tr className="text-white/35 text-[11px] uppercase tracking-[0.1em] border-b border-white/[0.07]">
                      <th className="text-left font-normal pb-2.5">Closed</th>
                      <th className="text-left font-normal pb-2.5">Batch</th>
                      <th className="text-right font-normal pb-2.5">Sales</th>
                      <th className="text-right font-normal pb-2.5">Refunds</th>
                      <th className="text-right font-normal pb-2.5">Net</th>
                      <th className="text-right font-normal pb-2.5">Reserve</th>
                      <th className="text-right font-normal pb-2.5">Deposit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {desk.batches.map((b) => {
                      const open = expanded === b.batchId;
                      return (
                        <React.Fragment key={b.batchId}>
                          <tr
                            className="border-b border-white/[0.04] cursor-pointer hover:bg-white/[0.025] transition"
                            onClick={() => setExpanded(open ? null : b.batchId)}
                          >
                            <td className="py-2.5 text-white/85 tabular-nums whitespace-nowrap">
                              {formatDay(b.day)}
                            </td>
                            <td className="py-2.5 text-white/60 tabular-nums whitespace-nowrap">
                              {b.processorBatchId}
                            </td>
                            <td className="py-2.5 text-right text-white/85 tabular-nums whitespace-nowrap">
                              {usd(b.salesCents)}
                            </td>
                            <td className="py-2.5 text-right tabular-nums whitespace-nowrap text-rose-300/75">
                              {b.refundsCents ? usd(b.refundsCents) : '—'}
                            </td>
                            <td
                              className={`py-2.5 text-right tabular-nums whitespace-nowrap ${
                                b.netCents < 0 ? 'text-rose-300' : 'text-white/85'
                              }`}
                            >
                              {usd(b.netCents)}
                            </td>
                            <td className="py-2.5 text-right tabular-nums whitespace-nowrap text-brand-200/70">
                              {b.reserveHeldCents ? `−${usd(b.reserveHeldCents)}` : '—'}
                            </td>
                            <td className="py-2.5 text-right whitespace-nowrap">
                              <span
                                className={`tabular-nums ${
                                  b.deposit.amountCents < 0 ? 'text-rose-300' : 'text-emerald-300'
                                }`}
                              >
                                {modelledUsd(b.deposit.amountCents, b.deposit.estimated)}
                              </span>
                              <span className="block text-white/30 text-[11px]">
                                {formatDay(b.deposit.day)}
                                {b.deposit.accountLast4 ? ` · ••${b.deposit.accountLast4}` : ''}
                              </span>
                            </td>
                          </tr>
                          {open && (
                            <tr className="border-b border-white/[0.04]">
                              <td colSpan={7} className="py-3">
                                <div className="space-y-1.5">
                                  {b.items.map((item) => (
                                    <div
                                      key={`${b.batchId}-${item.transactionId}`}
                                      className="rounded-lg bg-white/[0.03] px-3 py-2 flex items-center justify-between gap-3"
                                    >
                                      <div className="min-w-0">
                                        <p className="text-white/80 text-[13px] font-normal truncate">
                                          {item.customerName || 'Unknown customer'}
                                          {item.bookingReference && (
                                            <span className="text-brand-200 ml-2">
                                              {item.bookingReference}
                                            </span>
                                          )}
                                        </p>
                                        <p className="text-white/35 text-[11px] font-normal truncate">
                                          {item.description || item.transactionId}
                                        </p>
                                      </div>
                                      <p
                                        className={`text-[13px] font-normal tabular-nums shrink-0 ${
                                          item.isRefund ? 'text-rose-300' : 'text-white/80'
                                        }`}
                                      >
                                        {usd(item.amountCents)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-white/30 text-[11px] font-normal mt-4 leading-relaxed">
                Batches close nightly at 11PM Pacific, weekends included; the gateway stamps them in
                UTC, so they are converted back to local. Wires skip weekends, which is why a Friday
                and Saturday batch fund together, and Federal Reserve holidays: a deposit due on one
                lands the next business day.
                {desk?.assumptions?.nextBankHoliday
                  ? ` Next bank holiday: ${desk.assumptions.nextBankHoliday.name}, ${formatDayLong(desk.assumptions.nextBankHoliday.day)}.`
                  : ''}
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
